import React, { useEffect, useState } from "react";
import { useI18n } from "../lib/i18n";
import api from "../lib/api";
import { useTenant } from "../lib/tenant";
import { Save, Palette, Eye } from "lucide-react";
import { toast } from "sonner";
import LogoUploader from "../components/LogoUploader";

const FONT_CHOICES = ["Manrope", "Inter", "Roboto", "Poppins", "Montserrat", "Open Sans", "DM Sans", "Lato", "Space Grotesk"];

export default function BrandingSettings() {
  const { t } = useI18n();
  const { tenant, refresh } = useTenant();
  const [branding, setBranding] = useState(tenant?.branding || {});
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (tenant?.branding) setBranding(tenant.branding); }, [tenant]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/tenants/me/branding", branding);
      await refresh();
      toast.success(t("brand.updated"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-[#1e293b] tracking-tight">Branding · {tenant?.name}</h1>
          <p className="text-[13px] text-[#64748b] mt-1">{t("brand.subtitle")}</p>
        </div>
        <button data-testid="save-branding-btn" onClick={save} disabled={saving} className="sw-btn-primary">
          <Save size={14} /> {saving ? "Salvando..." : "Salvar branding"}
        </button>
      </div>

      <section className="sw-card">
        <h4 className="text-[11px] uppercase tracking-wider text-[#94a3b8] font-semibold mb-4 flex items-center gap-2"><Palette size={13} /> {t("brand.palette")}</h4>
        <div className="grid grid-cols-3 gap-4">
          <ColorField label={t("brand.primary")}     value={branding.primary}     onChange={(v) => setBranding({ ...branding, primary: v })} testid="color-primary" />
          <ColorField label={t("brand.secondary")}   value={branding.secondary}   onChange={(v) => setBranding({ ...branding, secondary: v })} testid="color-secondary" />
          <ColorField label="Acento"       value={branding.accent}      onChange={(v) => setBranding({ ...branding, accent: v })} testid="color-accent" />
          <ColorField label={t("brand.success")}      value={branding.success}     onChange={(v) => setBranding({ ...branding, success: v })} />
          <ColorField label="Alerta"       value={branding.warning}     onChange={(v) => setBranding({ ...branding, warning: v })} />
          <ColorField label="Erro"         value={branding.danger}      onChange={(v) => setBranding({ ...branding, danger: v })} />
          <ColorField label="Fundo"        value={branding.surface}     onChange={(v) => setBranding({ ...branding, surface: v })} />
          <ColorField label="Texto"        value={branding.text}        onChange={(v) => setBranding({ ...branding, text: v })} />
        </div>
      </section>

      <section className="sw-card">
        <h4 className="text-[11px] uppercase tracking-wider text-[#94a3b8] font-semibold mb-4">{t("brand.logo_font")}</h4>
        <div className="grid grid-cols-2 gap-4">
          <LogoUploader
            label="Logo completo (PNG/SVG horizontal ou stacked)"
            value={branding.logo_url}
            onChange={(v) => setBranding({ ...branding, logo_url: v })}
            testid="branding-logo-full"
            aspect="wide"
          />
          <LogoUploader
            label={t("brand.logo_mark")}
            value={branding.logo_mark_url}
            onChange={(v) => setBranding({ ...branding, logo_mark_url: v })}
            testid="branding-logo-mark"
            aspect="square"
          />
          <div className="col-span-2">
            <label className="sw-label">{t("brand.font")}</label>
            <select className="sw-input" value={branding.font_family || "Manrope"} onChange={(e) => setBranding({ ...branding, font_family: e.target.value })}>
              {FONT_CHOICES.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
            <p className="text-[11px] text-[#94a3b8] mt-1">{t("brand.google_fonts")}</p>
          </div>
        </div>
      </section>

      <section className="sw-card">
        <h4 className="text-[11px] uppercase tracking-wider text-[#94a3b8] font-semibold mb-4 flex items-center gap-2"><Eye size={13} /> {t("brand.live_preview")}</h4>
        <div className="rounded-xl border border-[#e2e8f0] overflow-hidden">
          <div className="px-5 py-4 flex items-center justify-between" style={{ background: branding.primary || "#475569", color: "white" }}>
            <div className="flex items-center gap-3">
              {branding.logo_mark_url && <img src={branding.logo_mark_url} alt="" className="w-9 h-9 object-contain" style={{ filter: "brightness(0) invert(1)" }} />}
              <div>
                <div className="font-semibold tracking-wide" style={{ fontFamily: `'${branding.font_family || "Manrope"}', sans-serif` }}>{tenant?.name}</div>
                <div className="text-[11px] opacity-80">{tenant?.tagline}</div>
              </div>
            </div>
            <button className="rounded-full px-4 py-1.5 text-[12px] font-medium" style={{ background: branding.accent || "#dac28a", color: branding.text || "#1e293b" }}>{t("brand.button")}</button>
          </div>
          <div className="p-5 grid grid-cols-3 gap-3" style={{ background: branding.surface || "#f6f6f6", color: branding.text || "#323232", fontFamily: `'${branding.font_family || "Manrope"}', sans-serif` }}>
            <PreviewBadge color={branding.success || "#34c759"} label={t("brand.approved")} />
            <PreviewBadge color={branding.warning || "#f59e0b"} label={t("brand.pending")} />
            <PreviewBadge color={branding.danger || "#881a27"} label={t("brand.rejected")} />
          </div>
        </div>
      </section>
    </div>
  );
}

function ColorField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="sw-label">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          data-testid={testid}
          value={value || "#000000"}
          onChange={(e) => onChange(e.target.value)}
          className="w-12 h-10 rounded border border-[#e2e8f0] cursor-pointer shrink-0"
        />
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="sw-input flex-1 font-mono text-[12px]"
          placeholder="#475569"
        />
      </div>
    </div>
  );
}

function PreviewBadge({ color, label }) {
  return (
    <div className="rounded-lg p-3 bg-white border border-[#e2e8f0] flex items-center gap-2">
      <span className="w-3 h-3 rounded-full" style={{ background: color }} />
      <div className="text-[12px]">{label}</div>
    </div>
  );
}
